/**
 * ProfileSettings
 *
 * Toggle switches for visibility settings in the user's profile.
 */

import { CalendarDays, BarChart3, ListChecks, Globe } from 'lucide-react';
import clsx from 'clsx';
import type { ProfileSettingsUI } from '@/db/profiles';

interface ProfileSettingsProps {
  settings: ProfileSettingsUI;
  onUpdate: (updates: Partial<ProfileSettingsUI>) => void;
}

export function ProfileSettings({ settings, onUpdate }: ProfileSettingsProps) {
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Visibility for org members */}
      <div>
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">
          Visibilita per i membri
        </h3>
        <p className="text-[11px] text-text-tertiary mb-4">
          Controlla cosa possono vedere i membri della tua organizzazione sul tuo profilo
        </p>

        <div className="space-y-3">
          <ToggleRow
            icon={<CalendarDays className="w-4 h-4" />}
            label="Mostra calendario"
            description="I membri possono vedere i tuoi task nel calendario"
            checked={settings.showCalendarToOrg}
            onChange={(v) => onUpdate({ showCalendarToOrg: v })}
          />
          <ToggleRow
            icon={<BarChart3 className="w-4 h-4" />}
            label="Mostra workload"
            description="I membri possono vedere le tue statistiche di carico"
            checked={settings.showWorkloadToOrg}
            onChange={(v) => onUpdate({ showWorkloadToOrg: v })}
          />
          <ToggleRow
            icon={<ListChecks className="w-4 h-4" />}
            label="Mostra task"
            description="I membri possono vedere la lista dei tuoi task"
            checked={settings.showTasksToOrg}
            onChange={(v) => onUpdate({ showTasksToOrg: v })}
          />
        </div>
      </div>

      {/* Public sharing */}
      <div className="pt-4 border-t border-border-primary">
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">
          Condivisione pubblica
        </h3>
        <p className="text-[11px] text-text-tertiary mb-4">
          Genera un link pubblico per condividere il tuo profilo con persone esterne
        </p>

        <ToggleRow
          icon={<Globe className="w-4 h-4" />}
          label="Abilita condivisione pubblica"
          description="Chiunque con il link puo vedere le informazioni selezionate"
          checked={settings.publicShareEnabled}
          onChange={(v) => onUpdate({ publicShareEnabled: v })}
        />

        {settings.publicShareEnabled && (
          <div className="mt-3 ml-10">
            <ToggleRow
              icon={<CalendarDays className="w-3.5 h-3.5" />}
              label="Mostra calendario pubblicamente"
              description="Il calendario sara visibile nel profilo pubblico"
              checked={settings.publicShowCalendar}
              onChange={(v) => onUpdate({ publicShowCalendar: v })}
              compact
            />
          </div>
        )}
      </div>

      {/* Working hours */}
      <div className="pt-4 border-t border-border-primary">
        <h3 className="text-[13px] font-semibold text-text-primary mb-1">
          Orario di lavoro
        </h3>
        <p className="text-[11px] text-text-tertiary mb-4">
          Imposta il tuo orario di lavoro predefinito
        </p>

        <div className="flex items-center gap-4">
          <div>
            <label className="block text-[10px] text-text-quaternary uppercase tracking-wider font-medium mb-1">
              Inizio
            </label>
            <input
              type="time"
              value={settings.workingHoursStart}
              onChange={(e) => onUpdate({ workingHoursStart: e.target.value })}
              className="px-3 py-1.5 rounded-lg bg-surface-tertiary/50 border border-border-primary text-[12px] text-text-primary focus:outline-none focus:border-accent-violet transition-colors"
            />
          </div>
          <div className="text-text-quaternary text-[12px] mt-5">-</div>
          <div>
            <label className="block text-[10px] text-text-quaternary uppercase tracking-wider font-medium mb-1">
              Fine
            </label>
            <input
              type="time"
              value={settings.workingHoursEnd}
              onChange={(e) => onUpdate({ workingHoursEnd: e.target.value })}
              className="px-3 py-1.5 rounded-lg bg-surface-tertiary/50 border border-border-primary text-[12px] text-text-primary focus:outline-none focus:border-accent-violet transition-colors"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Toggle Row ---

function ToggleRow({
  icon,
  label,
  description,
  checked,
  onChange,
  compact = false,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  compact?: boolean;
}) {
  return (
    <div
      className={clsx(
        'flex items-center gap-3 rounded-lg transition-colors',
        compact ? 'py-1.5' : 'p-3 bg-surface-secondary/30 border border-border-primary'
      )}
    >
      <div
        className={clsx(
          'flex items-center justify-center rounded-lg shrink-0',
          compact
            ? 'w-6 h-6 text-text-tertiary'
            : 'w-8 h-8 bg-surface-tertiary/50 text-text-tertiary'
        )}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className={clsx('font-medium text-text-primary', compact ? 'text-[11px]' : 'text-[12px]')}>
          {label}
        </p>
        <p className={clsx('text-text-quaternary', compact ? 'text-[9px]' : 'text-[10px]')}>
          {description}
        </p>
      </div>
      <button
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative inline-flex shrink-0 cursor-pointer rounded-full transition-colors duration-200',
          compact ? 'h-4 w-7' : 'h-5 w-9',
          checked ? 'bg-accent-violet' : 'bg-surface-tertiary'
        )}
      >
        <span
          className={clsx(
            'pointer-events-none inline-block transform rounded-full bg-white shadow-sm ring-0 transition-transform duration-200',
            compact ? 'h-3 w-3 mt-0.5' : 'h-4 w-4 mt-0.5',
            checked
              ? compact
                ? 'translate-x-3.5 ml-0'
                : 'translate-x-4.5 ml-0'
              : 'translate-x-0.5'
          )}
        />
      </button>
    </div>
  );
}
